<script setup lang="ts">
import { computed, onMounted, ref, watch, type PropType } from 'vue';
import type { ReviewInDb } from '@/api';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaCountLabel from '@/components/design/base/GaiaCountLabel.vue';
import GaiaKanbanCard from '@/components/design/review/GaiaKanbanCard.vue';
import GaiaKanbanColumn from '@/components/design/review/GaiaKanbanColumn.vue';
import { columnNote, columnStatus } from './reviewBoardMapping';
import { useReviewBoardData } from '@/composables/review/useReviewBoardData';
import {
  AGENT_EMAIL,
  AGENT_LABEL,
  type ReviewFilterOptions,
} from '@/composables/review/useReviewSearchFilters';
import { useAuthStore } from '@/stores/auth';
import { useKanbanStore } from '@/stores/kanbanStore';
import { logger } from '@/utils/logger';
import { initialsOf } from '@/utils/initials';
import { useGaiaToast } from '@/composables/useGaiaToast';

const props = defineProps({
  search: {
    type: String,
    default: '',
  },
  columnFilters: {
    type: Object as PropType<Record<string, string[]>>,
    default: () => ({}),
  },
  /** Cards per column page. */
  pageSize: {
    type: Number,
    default: 10,
  },
  density: {
    type: String as PropType<'comfortable' | 'compact'>,
    default: 'comfortable',
  },
});

const emit = defineEmits<{
  open: [review: ReviewInDb];
  delete: [review: ReviewInDb];
  details: [review: ReviewInDb];
  /** Per-column totals, for the columns menu's counts. */
  counts: [counts: Record<string, number>];
  /** Totals for the header line: how many reviews, and how many still running. */
  stats: [stats: { total: number; running: number }];
  'filter-options': [options: ReviewFilterOptions];
}>();

const authStore = useAuthStore();
const kanbanStore = useKanbanStore();
const message = useGaiaToast();

const search = computed(() => props.search);
const columnFilters = computed(() => props.columnFilters);
const pageSize = computed(() => props.pageSize);

const {
  reviewsByStateUid,
  statePagination,
  filterOptions,
  fetchColumn,
  loadMore,
  refreshAll,
  moveCard,
} = useReviewBoardData({ search, columnFilters, pageSize });

/**
 * `f_kanban_state_uid` means something different here than in the table: each
 * column already forces its own state into the query, so the filter can only
 * narrow *which columns are drawn*. Deliberate — it is what makes a filtered
 * board and a filtered table describe the same set.
 */
const columns = computed(() => {
  const all = kanbanStore.boardColumns;
  if (!Object.hasOwn(props.columnFilters, 'kanban_state_uid')) return all;
  const picked = props.columnFilters.kanban_state_uid;
  return picked.length > 0 ? all.filter(state => picked.includes(state.uid)) : all;
});

const canDrag = computed(() => authStore.userPermissions.can_update_reviews);

// ── Cards ───────────────────────────────────────────────────────────────────

function ownerLabel(review: ReviewInDb): string | undefined {
  const email = review.assignee_user_email;
  if (!email) return undefined;
  return email === AGENT_EMAIL ? AGENT_LABEL : email;
}

function ownerInitials(review: ReviewInDb): string | undefined {
  const email = review.assignee_user_email;
  if (!email) return undefined;
  return email === AGENT_EMAIL ? 'AI' : initialsOf(email);
}

/** Extraction progress, the only per-card progress the API reports. */
function progress(review: ReviewInDb): { finished: number; total: number } {
  return { finished: review.finished_count ?? 0, total: review.total_count ?? 0 };
}

function isRunning(review: ReviewInDb): boolean {
  const { finished, total } = progress(review);
  return total > 0 && finished < total;
}

function isSettled(review: ReviewInDb): boolean {
  const { finished, total } = progress(review);
  return total > 0 && finished >= total;
}

function dueLabel(review: ReviewInDb): string | undefined {
  if (!review.due_date) return undefined;
  const due = new Date(review.due_date);
  if (Number.isNaN(due.getTime())) return undefined;
  return `Due ${due.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
}

function isUrgent(review: ReviewInDb): boolean {
  if (!review.due_date) return false;
  const due = new Date(review.due_date);
  if (Number.isNaN(due.getTime())) return false;
  const endOfToday = new Date();
  endOfToday.setHours(23, 59, 59, 999);
  return due.getTime() <= endOfToday.getTime();
}

// ── Drag and drop ───────────────────────────────────────────────────────────

const draggedReview = ref<ReviewInDb | null>(null);
const dragOverStateUid = ref<string | null>(null);
const dragOverIndex = ref<number>(-1);
const isProcessingDrop = ref<boolean>(false);

function handleDragStart(event: DragEvent, review: ReviewInDb): void {
  if (!canDrag.value) {
    event.preventDefault();
    return;
  }
  event.dataTransfer?.setData('text/plain', review.uid);
  if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move';

  // Every reactive write is deferred out of `dragstart`: mutating state here
  // re-renders the dragged element mid-gesture and Chromium cancels the drag.
  setTimeout(() => {
    draggedReview.value = review;
  }, 0);
}

function handleDragEnd(): void {
  draggedReview.value = null;
  dragOverStateUid.value = null;
  dragOverIndex.value = -1;
}

function handleColumnDragOver(event: DragEvent, stateUid: string): void {
  if (!canDrag.value) return;
  event.preventDefault();
  dragOverStateUid.value = stateUid;
}

/**
 * A card reports the insertion point itself — before it or after it, by which
 * half of the card the pointer is over. Cheaper and steadier than the drop-zone
 * marker elements the old board rendered between every pair of cards.
 */
function handleCardDragOver(event: DragEvent, stateUid: string, index: number): void {
  if (!canDrag.value) return;
  event.preventDefault();
  event.stopPropagation();
  dragOverStateUid.value = stateUid;

  const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
  const isBottomHalf = event.clientY > rect.top + rect.height / 2;
  dragOverIndex.value = isBottomHalf ? index + 1 : index;
}

function handleDragLeave(): void {
  dragOverStateUid.value = null;
  dragOverIndex.value = -1;
}

async function handleDrop(event: DragEvent, stateUid: string): Promise<void> {
  event.preventDefault();
  event.stopPropagation();

  const review = draggedReview.value;
  const dropIndex = dragOverIndex.value;

  dragOverStateUid.value = null;
  dragOverIndex.value = -1;

  if (!review || isProcessingDrop.value) return;
  isProcessingDrop.value = true;

  try {
    const siblings = (reviewsByStateUid.value[stateUid] ?? []).map(r => r.uid);
    const position = dropIndex >= 0 && dropIndex <= siblings.length ? dropIndex : siblings.length;
    await moveCard(review.uid, stateUid, position, siblings);
  } catch (error) {
    logger.error('Failed to move review:', error);
    message.error('Failed to move ticket. Please try again.');
  } finally {
    draggedReview.value = null;
    isProcessingDrop.value = false;
  }
}

// ── Loading ─────────────────────────────────────────────────────────────────

async function load(): Promise<void> {
  try {
    await refreshAll();
  } catch {
    message.error('Failed to load reviews');
  }
}

async function loadColumnMore(stateUid: string): Promise<void> {
  try {
    await loadMore(stateUid);
  } catch {
    message.error('Failed to load more reviews');
  }
}

// A newly-revealed column has never been fetched; load just that one rather
// than refetching the whole board.
watch(columns, async (next, previous) => {
  const known = new Set(previous.map(state => state.uid));
  const fresh = next.filter(state => !known.has(state.uid));
  if (fresh.length === 0) return;
  const results = await Promise.allSettled(fresh.map(state => fetchColumn(state.uid)));
  // A failed column otherwise looks identical to an empty one.
  if (results.some(result => result.status === 'rejected')) message.error('Failed to load reviews');
});

watch(
  () => [props.search, props.columnFilters, props.pageSize] as const,
  () => void load(),
  { deep: true }
);

watch(
  [statePagination, reviewsByStateUid],
  () => {
    const counts: Record<string, number> = {};
    let total = 0;
    for (const [uid, pagination] of Object.entries(statePagination.value)) {
      counts[uid] = pagination.totalCount;
      total += pagination.totalCount;
    }
    emit('counts', counts);

    // Running is counted over loaded cards only — the board pages per column, so
    // a card behind "Load more" is not known to be running yet. Same limit the
    // mock's own counter has.
    const running = Object.values(reviewsByStateUid.value)
      .flat()
      .filter(review => isRunning(review)).length;
    emit('stats', { total, running });
  },
  { deep: true }
);

watch(filterOptions, value => {
  emit('filter-options', value);
});

defineExpose({ refresh: load });

onMounted(() => void load());
</script>

<template>
  <n-flex align="stretch" :size="16" :wrap="false" class="reviews-board">
    <gaia-kanban-column
      v-for="(state, columnIndex) in columns"
      :key="state.uid"
      :title="state.name"
      :count="statePagination[state.uid]?.totalCount ?? 0"
      :status="columnStatus(state, columnIndex, columns.length)"
      :note="columnNote(state)"
      :drag-over="dragOverStateUid === state.uid"
      :empty="(reviewsByStateUid[state.uid] ?? []).length === 0"
      :density="props.density"
      :style="{ flex: '1 1 0', minWidth: '260px' }"
      @dragover="handleColumnDragOver($event, state.uid)"
      @dragleave="handleDragLeave"
      @drop="handleDrop($event, state.uid)"
    >
      <gaia-kanban-card
        v-for="(review, cardIndex) in reviewsByStateUid[state.uid] ?? []"
        :key="review.uid"
        :title="review.name"
        :initials="ownerInitials(review)"
        :owner="ownerLabel(review)"
        :running="isRunning(review)"
        :settled="isSettled(review)"
        :due="dueLabel(review)"
        :urgent="isUrgent(review)"
        :dragging="draggedReview?.uid === review.uid"
        :density="props.density"
        :draggable="canDrag"
        @dragstart="handleDragStart($event, review)"
        @dragend="handleDragEnd"
        @dragover="handleCardDragOver($event, state.uid, cardIndex)"
        @open="emit('open', review)"
        @delete="emit('delete', review)"
      >
        <template #meta>
          <!-- The API reports no risk count, so `risk-count` stays unbound and
               the card's risk chip does not render. Extraction progress is what
               the board actually knows about a running review. -->
          <gaia-count-label v-if="progress(review).total > 0">
            {{ progress(review).finished }}/{{ progress(review).total }} extracted
          </gaia-count-label>
        </template>
        <template #actions>
          <gaia-button size="small" quaternary @click="emit('details', review)"
            >Details</gaia-button
          >
        </template>
      </gaia-kanban-card>

      <template v-if="statePagination[state.uid]?.hasMore" #footer>
        <gaia-button size="tiny" block @click="loadColumnMore(state.uid)">
          Load
          {{
            Math.min(
              props.pageSize,
              (statePagination[state.uid]?.totalCount ?? 0) -
                (reviewsByStateUid[state.uid] ?? []).length
            )
          }}
          more
        </gaia-button>
      </template>
    </gaia-kanban-column>
  </n-flex>
</template>

<style scoped>
/* The columns scroll internally, so the row must not: it takes the height its
   mount gives it and hands each column `max-height: 100%`. */
.reviews-board {
  height: 100%;
  min-height: 0;
  overflow-x: auto;
}
</style>
